import React, { useMemo } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';
import { ChatPost, Document } from '../../stores/reducers/chat';
import Socket, { ConnectedUser } from '../../connection/socket';
import Avatar from '../general-components/avatar';
import Thumbnail from './chat-document-thumbnail';
import { showAlert } from '../general-components/alert-service';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { GreenroomState as GS } from '../../stores/types/types';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import * as ModalActions from '../../stores/actions/modal';

interface ChatMessageProps {
	post: ChatPost;
	user?: ConnectedUser;
	isChild?: boolean | null;
	socket: Socket | null;
}

const getDocIcon = (doc: Document): string => {
	const fileExtention = doc.location.split('.').pop();
	switch (fileExtention) {
		case 'pdf':
			return 'icon-file-pdf';
		case 'xls': case 'xlsx':
			return 'icon-file-excel';
		case 'ppt': case 'pptx':
			return 'icon-file-powerpoint';
		case 'doc': case 'docx':
			return 'icon-file-word';
		case 'jpg': case 'jpeg': case 'png': case 'gif':
			return 'icon-image';
		case 'zip': case 'sitx': case '7z': case 'rar': case 'gz':
			return 'icon-file-zip';
		default:
			return 'icon-file-plain';
	}
};

function downloadAttachment(attachment: Document) {
	const url = !attachment.location.includes('https://')
		? `https://greenroom-assets-prod.brand.live/legacy-uploads/${attachment.location}`
		: attachment.location;
	fetch(url)
		.then((resp) => resp.blob())
		.then((blob) => {
			const link = document.createElement('a');
			link.target = '_blank';
			// name the file the original name, not the display name, as the display name might not
			// contain the file suffix
			link.download = attachment.original_name || attachment.originalName || 'Untitled';
			link.href = window.URL.createObjectURL(blob);
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(link);
		}).catch((e) => {
			console.error(e);
			showAlert({
				message: 'Download Error',
				description: 'We ran into an issue downloading that attachment.',
				duration: 500,
			});
		});
}

function parseDate(date: number | string): string {
	return new Date(date || Date.now()).toLocaleTimeString(navigator.language, { hour: 'numeric', minute: '2-digit' });
}

function getName(name1: any, name2: any): string {
	let fileName = name1 || name2;
	const fileNameSplit = fileName.split('.');
	fileNameSplit.pop();
	fileName = fileNameSplit.join('');
	return `${fileName}.`;
}

export default function ChatMessage(props: ChatMessageProps) {
	const dispatch = useDispatch();

	const {
		post,
		user: messageUser,
		isChild,
		socket,
	} = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const user = useSelector((s: GS) => s.SessionReducer.user);

	const handleOpenDeleteModal = () => {
		dispatch(ModalActions.setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.DELETE_CHAT_MESSAGE_CONFIRMATION,
			modalData: {
				chatPost: post,
				socket,
			},
		}));
	};

	const canDelete = useMemo(() => messageUser?.gr_user === user?.gr_user || host, [host, user]);

	return (
		<div className="chat-message-container">
			{isChild ? (
				<div className="avatar-spacer"></div>
			) : (
				<>
					{messageUser && (
						<Avatar
							user={messageUser}
							size="chat"
							noBorder={true}
						/>
					)}
				</>
			)}

			<div className={classNames('chat-message', { 'has-attachment': !!post.attachment })}>
				{!isChild && (
					<div className="chat-message-meta">
						<label>{messageUser?.first_name} {messageUser?.last_name}</label>
						<span>{parseDate(post.created)}</span>
					</div>
				)}

				<div className="chat-message-content">
					<ReactMarkdown
						components={{
							a: (componentProps: any) => <a href={componentProps.href} rel="noopener noreferrer" target="_blank">
									{componentProps.children}
								</a>,
						}}
					>
						{post.message}
					</ReactMarkdown>
				</div>

				{post.attachment && post.attachment.location && (
					<div className='doc-wrapper in-message'>
						<button className="doc-background" onClick={() => downloadAttachment(post.attachment as Document)}>
							{post.attachment.thumbnail && post.attachment.thumbnail_status > 0 ? (
								<Thumbnail thumbnail={post.attachment.thumbnail} />
							) : null}
							<div className={classNames('doc-title')}>
								<div className={classNames('doc-type', getDocIcon(post.attachment))}>
									{post.attachment.location.split('.').pop()}
								</div>
								<div className="doc-name">
									<label>{getName(
										post.attachment.original_name,
										post.attachment.originalName,
									)}</label>
								</div>
								<div className="doc-extension">
									<label>{post.attachment.location.split('.').pop()}</label>
								</div>
							</div>
						</button>
					</div>
				)}
			</div>
			<div
				className="flex-align-center"
				style={{
					marginLeft: !canDelete ? '32px' : '0px',
					marginBottom: '10px',
				}}
			>
				{canDelete && (
					<div
						className="delete-message"
						onClick={handleOpenDeleteModal}
						data-testid="delete-chat-message"
					>
						<Icon name={ICONS.TRASH} color={COLORS.TEXT_GRAY_300} size={12} />
					</div>
				)}
			</div>
		</div>
	);
}
